import React, { useState, useEffect, useRef } from 'react';
import { getFormattedPriceObject } from '@/helpers/currencyHelpers';
import { useSelector, useDispatch } from 'react-redux';
import { haversine } from '@/helpers/locationHelpers';
import { useFetchAllSpacesSubtypeRelationships } from '@/services/spacesSubtypeRelationshipService';
import { getOAuthTokenGoogle } from '@/services/userService';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import {
  MMAXIMUM_DISTANCE_SEARCH_SPACES,
  DEFAULT_MAP_COORDINATES,
  MAX_DISTANCE_THRESHOLD,
} from '@/types/distance.constants';

export const useWorkspaceMap = (searchFormData, currencyData, isFromMexico) => {
  const dispatch = useDispatch();
  const currentSearchFormData = useSelector(state => state.searchFormData);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [workspacesDataFromBackend, setWorkSpacesDataFromBackend] = useState(
    [],
  );
  const [coordinatesForMapCenter, setCoordinatesForMapCenter] = useState({});
  const [isSignInModalIsOpen, setIsSignInModalIsOpen] = useState(false);
  const [isWarningModalIsOpen, setIsWarningModalIsOpen] = useState(true);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(true);

  const { data, error } = useFetchAllSpacesSubtypeRelationships();

  const startAutoMove = swiper => {
    if (!swiper || swiper.destroyed) return;
    if (swiper.customReqId) cancelAnimationFrame(swiper.customReqId);
    const SPEED = 40;

    const animate = () => {
      if (!swiper || swiper.destroyed) return;
      const currentTranslate = swiper.getTranslate();
      const maxTranslate = swiper.maxTranslate();
      let nextTranslate = currentTranslate - SPEED;
      if (nextTranslate <= maxTranslate) {
        swiper.setTranslate(0);
        swiper.slideTo(0, 0, false);
        if (swiper.customReqId) {
          cancelAnimationFrame(swiper.customReqId);
          swiper.customReqId = null;
        }
        return;
      }
      swiper.setTranslate(nextTranslate);
      swiper.updateActiveIndex(nextTranslate);
      swiper.customReqId = requestAnimationFrame(animate);
    };
    swiper.customReqId = requestAnimationFrame(animate);
  };

  const onSwiperInit = swiper => {
    if (searchFormData?.coordinates !== DEFAULT_MAP_COORDINATES) {
      startAutoMove(swiper);
    }
  };

  const getCoordinatesForMapCenter = (coordinatesFromFormData, spacesData) => {
    const filteredByType = spacesData?.filter(
      space =>
        space?.subtype_title.toLowerCase() ===
        searchFormData?.spaceType.toLowerCase(),
    );

    if (!filteredByType || filteredByType.length === 0) {
      return null;
    }

    const closestSpace = filteredByType.reduce((prev, current) => {
      const currentDist = haversine(
        coordinatesFromFormData.lat,
        coordinatesFromFormData.lng,
        parseFloat(current?.space_latitude),
        parseFloat(current?.space_logitude),
      );

      const prevDist =
        prev.distance !== undefined
          ? prev.distance
          : haversine(
              coordinatesFromFormData.lat,
              coordinatesFromFormData.lng,
              parseFloat(prev?.space_latitude),
              parseFloat(prev?.space_logitude),
            );

      return currentDist < prevDist
        ? { ...current, distance: currentDist }
        : { ...prev, distance: prevDist };
    });

    if (closestSpace.distance < MAX_DISTANCE_THRESHOLD) {
      const coords = {
        lat: parseFloat(closestSpace.space_latitude),
        lng: parseFloat(closestSpace.space_logitude),
      };
      return coords;
    }

    return {
      lat: coordinatesFromFormData.lat,
      lng: coordinatesFromFormData.lng,
    };
  };

  const formatSpacesData = (coordinates, spacesData) => {
    const filteredSpacesByDistance = spacesData
      ?.filter(
        space =>
          space?.subtype_title.toLowerCase() ===
          searchFormData?.spaceType.toLowerCase(),
      )
      ?.map(space => {
        const distance = haversine(
          coordinates.lat,
          coordinates.lng,
          parseFloat(space?.space_latitude),
          parseFloat(space?.space_logitude),
        );
        return { ...space, distance };
      })
      ?.filter(space => space?.distance <= MMAXIMUM_DISTANCE_SEARCH_SPACES)
      .sort((a, b) => a.distance - b.distance);

    let orderedSpacesDataByCountry = isFromMexico
      ? filteredSpacesByDistance.sort((a, b) => {
          if (a.space_id === 151 && b.space_id !== 151) return -1;
          if (a.space_id !== 151 && b.space_id === 151) return 1;
          return 0;
        })
      : filteredSpacesByDistance.sort((a, b) => {
          if (a.space_id === 154 && b.space_id !== 154) return -1;
          if (a.space_id !== 154 && b.space_id === 154) return 1;
          return 0;
        });

    const normalItems = orderedSpacesDataByCountry.filter(
      item => !item.relationship_title?.includes('Network'),
    );

    const networkItems = orderedSpacesDataByCountry.filter(item =>
      item.relationship_title?.includes('Network'),
    );

    orderedSpacesDataByCountry = [...normalItems, ...networkItems];

    const sortedData = searchFormData?.seletedSpaceId
      ? [...orderedSpacesDataByCountry].sort((a, b) => {
          if (a.id_relationship === searchFormData?.seletedSpaceId) return -1;
          if (b.id_relationship === searchFormData?.seletedSpaceId) return 1;
          return 0;
        })
      : orderedSpacesDataByCountry;

    return sortedData.map(space => {
      const formattedPriceObject = getFormattedPriceObject(
        space,
        currencyData?.currency,
      );
      return {
        ...space,
        calculatedPrice: formattedPriceObject?.calculatedPrice,
        currency: formattedPriceObject?.currency,
        formattedPrice: formattedPriceObject?.formattedPrice,
      };
    });
  };

  const openSignInModal = () => {
    setIsSignInModalIsOpen(true);
  };

  const closeSignInModal = () => {
    setIsSignInModalIsOpen(false);
  };

  const closeWarningModal = () => {
    setIsWarningModalIsOpen(false);
  };

  const onClickMarkers = (id, coordinates) => {
    dispatch(
      setSearchFormData({
        ...currentSearchFormData,
        coordinates: coordinates,
        seletedSpaceId: id,
      }),
    );
  };

  const loginWithGoogleOAuth = async ({ username, googleToken }) => {
    setLoading(true);
    setErrors(null);

    try {
      const tokenData = await getOAuthTokenGoogle({
        username,
        googleToken,
      });

      return tokenData;
    } catch (err) {
      console.error('Error en loginWithGoogleOAuth:', err);
      setErrors(err.message || 'Google OAuth failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      const calculatedCoords =
        searchFormData?.coordinates === DEFAULT_MAP_COORDINATES
          ? searchFormData?.coordinates
          : getCoordinatesForMapCenter(searchFormData?.coordinates, data);

      setWorkSpacesDataFromBackend(formatSpacesData(calculatedCoords, data));

      const hasCoordsChanged =
        calculatedCoords?.lat !== coordinatesForMapCenter?.lat ||
        calculatedCoords?.lng !== coordinatesForMapCenter?.lng;

      if (hasCoordsChanged) {
        setCoordinatesForMapCenter(calculatedCoords);
      }

      setIsLoading(false);
    } else if (error) {
      setIsLoading(false);
    }
  }, [data, searchFormData, currencyData, error, coordinatesForMapCenter]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const scrollTrigger = isMobile ? 1360 : 600;
    const handleScroll = () => {
      if (isSignInModalIsOpen) {
        if (window.scrollY < scrollTrigger) {
          closeSignInModal();
        }
      } else {
        if (window.scrollY > scrollTrigger) {
          openSignInModal();
        }
      }
    };

    if (isScrollListenerActive) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSignInModalIsOpen, isScrollListenerActive]);

  return {
    isLoading,
    setIsLoading,
    workspacesDataFromBackend,
    isSignInModalIsOpen,
    openSignInModal,
    closeSignInModal,
    closeWarningModal,
    isWarningModalIsOpen,
    coordinatesForMapCenter,
    onClickMarkers,
    onSwiperInit,
    loginWithGoogleOAuth,
  };
};
